import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileContract, 
  faEdit, 
  faCalendarAlt, 
  faSave, 
  faExternalLinkAlt,
  faCheck,
  faTimes,
  faInfoCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import useBailSummary from '../hooks/useBailSummary';

const BailSummary = ({
  bail,
  locataire,
  onUpdate,
  estCloture,
  onClotureChange,
  isLoading
}) => {
  // Use our custom hook for bail summary
  const {
    showStartDateEdit,
    showEndDateEdit,
    showFactureClotureEdit,
    newStartDate,
    newEndDate,
    newFactureCloture,
    updating,
    setNewStartDate,
    setNewEndDate,
    setNewFactureCloture,
    toggleStartDateEdit,
    toggleEndDateEdit,
    toggleFactureClotureEdit,
    updateBailStartDate,
    updateBailEndDate,
    updateFactureCloture
  } = useBailSummary(bail, onUpdate);

  const handleClotureChange = (e) => {
    const isChecked = e.target.checked;
    console.log("Checkbox clôture changé:", isChecked);
    console.log("ID locataire:", locataire?.id);
    onClotureChange && onClotureChange(isChecked);
  };

  if (!bail) return <article aria-busy="true">Aucune donnée de bail disponible</article>;

  return (
    <article className="bloc">
      <header>
        <h2>
          <FontAwesomeIcon icon={faFileContract} /> Résumé du bail
          <a
            href={`https://airtable.com/app7XWoxwjvdzoSQ1/tblstPzxIVWW2XgaG/viwS3DTLpSML9A1WX/${bail.id}?blocks=hide`}
            target="_blank"
            rel="noopener noreferrer"
            className="outline"
            role="button"
            aria-label="Voir le bail dans Airtable"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} /> Voir le bail
          </a>
        </h2>
      </header>
      
      {/* Ajouter un indicateur de chargement si isLoading est true */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>Mise à jour des données...</span>
        </div>
      )}
      
      <table className="resume-table">
        <tbody>
          <tr>
            <td className="label">Locataire</td>
            <td className="value">{locataire?.nom || '-'}</td>
          </tr>
          
          <tr>
            <td className="label">Période</td>
            <td className="value">
              <span>
                {bail.periode?.debut || '-'}
            <button 
              onClick={toggleStartDateEdit} 
              className="outline secondary icon-only" 
              title="Modifier la date de début"
              aria-label="Modifier la date de début"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            {showStartDateEdit && (
              <div className="form-group">
                <input
                  type="date"
                  id="newBailStartDate"
                  name="newBailStartDate"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  placeholder=""
                />
                <button
                  className="outline secondary small"
                  onClick={updateBailStartDate}
                  disabled={updating || !newStartDate}
                  aria-busy={updating}
                >
                  {updating ? (
                    'Mise à jour...'
                  ) : (
                    <><FontAwesomeIcon icon={faCalendarAlt} /> Changer date début</>
                  )}
                </button>
              </div>
            )}
              </span>
              
              → 
              
              <span>
                {bail.periode?.fin || '-'}
            <button 
              onClick={toggleEndDateEdit} 
              className="outline secondary icon-only"
              title="Modifier la date de fin"
              aria-label="Modifier la date de fin"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            {showEndDateEdit && (
              <div className="form-group">
                <input
                  type="date"
                  id="newBailEndDate"
                  name="newBailEndDate"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  placeholder=""
                />
                <button
                  className="outline secondary small"
                  onClick={updateBailEndDate}
                  disabled={updating || !newEndDate}
                  aria-busy={updating}
                >
                  {updating ? (
                    'Mise à jour...'
                  ) : (
                    <><FontAwesomeIcon icon={faCalendarAlt} /> Changer date fin</>
                  )}
                </button>
              </div>
            )}
              </span>
            </td>
          </tr>
          
          <tr>
            <td className="label">Appartement</td>
            <td className="value">
              Ch. {bail.appartement?.chambre || '-'} – {bail.appartement?.nom || '-'}
            </td>
          </tr>
          
          <tr>
            <td className="label">Loyer CC</td>
            <td className="value">{bail.montants?.loyer || 0} €</td>
          </tr>
          
          <tr>
            <td className="label">Premier loyer CC</td>
            <td className="value">{bail.montants?.premierLoyer || 0} €</td>
          </tr>
          
          <tr>
            <td className="label">Dernier loyer CC</td>
            <td className="value">{bail.montants?.dernierLoyer || 0} €</td>
          </tr>
          
          <tr>
            <td className="label">Caution</td>
            <td className="value">{bail.montants?.caution || 0} €</td>
          </tr>
          
          <tr>
            <td className="label">Frais dossier</td>
            <td className="value">{bail.montants?.fraisDossier || 0} €</td>
          </tr>
          
          <tr>
            <td className="label">Bail terminé</td>
            <td className="value">
              {bail.estTermine ? (
                <span className="badge badge-success">
                  <FontAwesomeIcon icon={faCheck} /> Oui
                </span>
              ) : (
                <span className="badge badge-danger">
                  <FontAwesomeIcon icon={faTimes} /> Non
                </span>
              )}
            </td>
          </tr>
          
          <tr>
            <td className="label">Statut clôture</td>
            <td className="value">
              <label for="cloture-checkbox" className="switch-label">
                <input
                  type="checkbox"
                  id="cloture-checkbox"
                  role="switch"
                  checked={estCloture}
                  onChange={handleClotureChange}
                  disabled={isLoading} // Désactiver pendant le chargement
                />
                <span className="checkbox-status">
                  {estCloture ? 'Clôturé' : 'Non clôturé'}
                </span>
              </label>
            </td>
          </tr>
          
          <tr>
            <td className="label">Version</td>
            <td className="value">
              {locataire?.flags?.version ? (
                <>
                  <span className="badge badge-v2">{locataire.flags.version}</span>
                  <span className="tooltip">
                    <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
                    <span className="tooltiptext">
                      Comptabilité en deux systèmes - Standard et V2 (paiements manuels, espèces, PayPal)
                    </span>
                  </span>
                </>
              ) : (
                'Standard'
              )}
            </td>
          </tr>
          
          {bail.estTermine && (
            <tr>
              <td className="label">Montant facture de clôture</td>
              <td className="value">
                {bail.montants?.factureCloture || 0} €
            <button 
              onClick={toggleFactureClotureEdit} 
              className="outline secondary icon-only"
              title="Modifier le montant de la facture de clôture"
              aria-label="Modifier le montant de la facture de clôture"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            {showFactureClotureEdit && (
              <div className="form-group">
                <input 
                  type="number" 
                  id="newFactureCloture" 
                  name="newFactureCloture"
                  step="0.01" 
                  min="0" 
                  value={newFactureCloture}
                  onChange={(e) => setNewFactureCloture(e.target.value)}
                />
                <button 
                  className="outline secondary small"
                  onClick={updateFactureCloture}
                  disabled={updating}
                  aria-busy={updating}
                >
                  {updating ? (
                    'Mise à jour...'
                  ) : (
                    <><FontAwesomeIcon icon={faSave} /> Modifier</>
                  )}
                </button>
              </div>
            )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </article>
  );
};

export default BailSummary;
